// 剧本生成任务创建页
// 表单提交输入，支持文件上传和文本输入

import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Radio,
  Upload,
  message,
  Progress,
  Alert,
} from 'antd'
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import type { UploadProps, RadioChangeEvent } from 'antd'
import { createScriptGenerationTask, uploadScriptFile, startScriptGenerationTask } from '@/services/scriptGeneration'
import type { CreateScriptGenerationTaskRequest } from '@/types/scriptGeneration'
import {
  SCRIPT_GENERATION_CONFIG,
  isValidInputType,
  type InputType,
} from '@/constants/scriptGeneration'
import { handleApiError } from '@/utils/errorHandler'
import './CreateTask.css'

const { TextArea } = Input
const { Option } = Select

/**
 * 剧本生成任务创建页
 */
const ScriptGenerationCreateTask: React.FC = () => {
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()
  const [form] = Form.useForm()

  // 状态管理
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [inputType, setInputType] = useState<InputType>('text')
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string>('')

  // 文件上传取消控制
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * 组件卸载时取消上传
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  /**
   * 返回列表页
   */
  const handleBack = useCallback(() => {
    navigate('/script-generation')
  }, [navigate])

  /**
   * 输入类型切换（使用类型守卫）
   */
  const handleInputTypeChange = useCallback((e: RadioChangeEvent) => {
    const value = e.target.value
    if (isValidInputType(value)) {
      setInputType(value)
      // 清空文件上传状态
      if (value === 'text') {
        setUploadedFileId(null)
        setUploadedFileName('')
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }
      }
    }
  }, [])

  /**
   * 文件上传处理（支持取消）
   */
  const uploadProps: UploadProps = {
    name: 'file',
    accept: SCRIPT_GENERATION_CONFIG.ALLOWED_FILE_EXTENSIONS.join(','),
    maxCount: 1,
    beforeUpload: (file) => {
      // 检查文件大小
      if (file.size > SCRIPT_GENERATION_CONFIG.MAX_FILE_SIZE) {
        messageApi.error(`文件大小不能超过 ${SCRIPT_GENERATION_CONFIG.MAX_FILE_SIZE / 1024 / 1024}MB`)
        return Upload.LIST_IGNORE
      }

      // 创建新的 AbortController
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      const controller = new AbortController()
      abortControllerRef.current = controller

      // 开始上传
      setUploading(true)
      setUploadProgress(0)

      uploadScriptFile(file, (progress) => {
        setUploadProgress(progress)
      }, controller.signal)
        .then((result) => {
          setUploadedFileId(result.file_id)
          setUploadedFileName(result.file_name)
          messageApi.success('文件上传成功')
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            handleApiError(error, '文件上传失败', messageApi)
          }
        })
        .finally(() => {
          setUploading(false)
          abortControllerRef.current = null
        })

      return false // 阻止自动上传
    },
    onRemove: () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      setUploadedFileId(null)
      setUploadedFileName('')
    },
  }

  /**
   * 表单提交
   * 表单字段: name, description, inputContent
   * API 字段: task_name, task_description, input_text
   */
  const handleSubmit = useCallback(async (values: {
    name: string
    description?: string
    inputContent?: string
  }) => {
    // 验证
    if (inputType === 'text' && !values.inputContent?.trim()) {
      messageApi.error('请输入剧本内容')
      return
    }

    if (inputType === 'file' && !uploadedFileId) {
      messageApi.error('请上传剧本文件')
      return
    }

    setLoading(true)
    let taskId: string | null = null
    try {
      // 表单字段映射到 API 字段
      const payload: CreateScriptGenerationTaskRequest = {
        task_name: values.name,
        task_description: values.description,
        input_type: inputType, // 直接使用 inputType，因为已统一为 'text' | 'file'
        input_text: inputType === 'text' ? values.inputContent : undefined,
        input_file_url: inputType === 'file' ? uploadedFileId! : undefined,
      }

      const response = await createScriptGenerationTask(payload)
      taskId = response.task_id
      messageApi.success('任务创建成功')

      // 启动任务
      try {
        await startScriptGenerationTask(taskId)
        messageApi.success('任务已启动')
      } catch (startError) {
        console.error('启动任务失败:', startError)
        messageApi.warning('任务创建成功但启动失败，请在详情页手动启动')
      }

      // 跳转到任务详情页
      navigate(`/script-generation/${taskId}`)
    } catch (error) {
      handleApiError(error, '任务创建失败', messageApi)
    } finally {
      setLoading(false)
    }
  }, [inputType, uploadedFileId, navigate, messageApi])

  return (
    <div className="script-generation-create-task">
      {contextHolder}

      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
              返回
            </Button>
            <span>创建剧本生成任务</span>
          </Space>
        }
      >
        <Alert
          message="AI 剧本生成"
          description="根据您的输入内容（文本或文档），AI 将自动分析并生成 O2World 剧本包，包含角色配置、对话内容、事件触发器等完整游戏内容。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            inputType: 'text',
          }}
        >
          {/* 基本信息 */}
          <Card type="inner" title="基本信息" style={{ marginBottom: 16 }}>
            <Form.Item
              label="任务名称"
              name="name"
              rules={[
                { required: true, message: '请输入任务名称' },
                { max: 100, message: '任务名称不能超过 100 个字符' },
              ]}
            >
              <Input placeholder="例如：51 号站悬疑剧本" />
            </Form.Item>

            <Form.Item
              label="任务描述"
              name="description"
              rules={[{ max: 500, message: '任务描述不能超过 500 个字符' }]}
            >
              <TextArea
                rows={3}
                placeholder="简要描述剧本的主题、风格、主要情节等（可选）"
              />
            </Form.Item>
          </Card>

          {/* 输入内容 */}
          <Card type="inner" title="输入内容" style={{ marginBottom: 16 }}>
            <Form.Item label="输入类型">
              <Radio.Group value={inputType} onChange={handleInputTypeChange}>
                <Radio.Button value="text">文本输入</Radio.Button>
                <Radio.Button value="file">文档上传</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {inputType === 'text' && (
              <Form.Item
                label="剧本内容"
                name="inputContent"
                rules={[{ required: true, message: '请输入剧本内容' }]}
              >
                <TextArea
                  rows={12}
                  placeholder="在此输入您的剧本创意、大纲或详细描述...

示例：
剧本名称：51 号站悬疑事件
背景设定：2050 年，废弃的太空站
主要角色：
- 玩家：调查员，收到神秘求救信号
- AI 管家：看似友好，但隐藏着秘密
- 科学家：唯一的幸存者，记忆混乱

主要情节：
1. 玩家抵达太空站，探索环境
2. 发现线索，揭开 51 号站的秘密
3. 面临选择：拯救 AI 或摧毁空间站"
                />
              </Form.Item>
            )}

            {inputType === 'file' && (
              <Form.Item label="上传文件">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ color: '#666', fontSize: 12 }}>
                    支持格式：{SCRIPT_GENERATION_CONFIG.ALLOWED_FILE_EXTENSIONS.join(' ')}
                    （最大 {SCRIPT_GENERATION_CONFIG.MAX_FILE_SIZE / 1024 / 1024}MB）
                  </div>

                  {uploading && (
                    <div style={{ width: 400 }}>
                      <Progress percent={uploadProgress} status="active" />
                      <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                        正在上传...
                      </div>
                    </div>
                  )}

                  {!uploading && (
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />}>选择文件</Button>
                    </Upload>
                  )}

                  {uploadedFileName && (
                    <Alert
                      message={`已上传: ${uploadedFileName}`}
                      type="success"
                      showIcon
                      style={{ marginTop: 8 }}
                    />
                  )}

                  <Form.Item
                    name="fileId"
                    hidden
                    initialValue={uploadedFileId}
                  >
                    <Input />
                  </Form.Item>
                </Space>
              </Form.Item>
            )}
          </Card>

          {/* 高级设置（可选） */}
          <Card type="inner" title="高级设置" style={{ marginBottom: 24 }}>
            <Form.Item label="剧本难度" name="difficulty">
              <Select placeholder="选择剧本难度（可选）" allowClear>
                <Option value="easy">简单</Option>
                <Option value="medium">中等</Option>
                <Option value="hard">困难</Option>
                <Option value="expert">专家</Option>
              </Select>
            </Form.Item>

            <Form.Item label="剧本时长" name="duration">
              <Select placeholder="预计游戏时长（可选）" allowClear>
                <Option value="30">30 分钟</Option>
                <Option value="60">1 小时</Option>
                <Option value="120">2 小时</Option>
                <Option value="180">3 小时</Option>
                <Option value="240">4 小时以上</Option>
              </Select>
            </Form.Item>
          </Card>

          {/* 提交按钮 */}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} size="large">
                创建任务
              </Button>
              <Button onClick={handleBack} size="large">
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}

export default ScriptGenerationCreateTask
